import {
  App as AntdApp,
  Button,
  Card,
  Empty,
  Flex,
  Input,
  Modal,
  Tooltip,
  Typography
} from 'antd'
import type { TableColumnsType } from 'antd'
import { Bot, ExternalLink, Plus, Search, Settings, Trash2 } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import type { WorkspaceAgent } from 'src/api/facade/types'
import {
  ContentSpinner,
  DateTimeCell,
  ProTableContainer,
  QueryErrorAlert,
  SoonTag,
  TableActionColumnTitle
} from 'src/components'
import { FILTER_CONTROL_WIDTH } from 'src/constants'
import {
  DEFAULT_PER_PAGE,
  DEFAULT_PER_PAGE_OPTIONS,
  useHasWorkspacePermissions
} from 'src/hooks'
import { paths } from 'src/router/paths'
import { useShowApiErrors, WorkspacePermissionCode } from 'src/utils'

import {
  AgentsHowItWorks,
  useDeleteAgent,
  useWorkspaceAgents
} from '../components/agents'
import { useWorkspacePendingApprovals } from '../components/agents/useAgentGovernance'

import { WorkspacePageShell, useWorkspaceRoute } from './WorkspacePageShell'

const { Text } = Typography

const KpiCell = ({
  label,
  value,
  soon
}: {
  label: string
  value?: number | string
  soon?: boolean
}) => (
  <Flex vertical gap={4} style={{ flex: 1, padding: '12px 16px', minWidth: 0 }}>
    <Text type="secondary" className="ui-eyebrow">
      {label}
    </Text>
    {soon ? (
      <div style={{ lineHeight: '22px' }}>
        <SoonTag style={{ marginInlineStart: 0 }} />
      </div>
    ) : (
      <Text strong style={{ fontSize: 20, lineHeight: 1.1 }}>
        {value}
      </Text>
    )}
  </Flex>
)

export const WorkspaceAgentsPage = () => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const navigate = useNavigate()
  const showApiErrors = useShowApiErrors()
  const { id, workspaceId } = useWorkspaceRoute()
  const [searchParams, setSearchParams] = useSearchParams()

  // Agents are provisioned alongside applications, so they follow the same
  // codes: `applications.view` opens the board, `applications.manage` writes.
  const mayManageAgents = useHasWorkspacePermissions(
    WorkspacePermissionCode.APPLICATIONS_MANAGE
  )

  const q = searchParams.get('q') ?? ''

  const setQuery = (value: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (value) next.set('q', value)
        else next.delete('q')
        return next
      },
      { replace: true }
    )

  const {
    data: agents = [],
    isLoading,
    isError,
    refetch
  } = useWorkspaceAgents(workspaceId)
  const deleteMutation = useDeleteAgent(workspaceId)
  const pendingApprovals = useWorkspacePendingApprovals(workspaceId)

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return agents

    return agents.filter(
      (a) =>
        a.name.toLowerCase().includes(needle) ||
        (a.description ?? '').toLowerCase().includes(needle)
    )
  }, [agents, q])

  const openDetail = (agent: WorkspaceAgent) =>
    navigate(paths.workspaces.agentDetail(id, agent.id))

  const confirmDelete = (agent: WorkspaceAgent) =>
    Modal.confirm({
      title: t('workspace.agents.delete.confirmTitle'),
      content: t('workspace.agents.delete.confirmBody', { name: agent.name }),
      okText: t('workspace.agents.actions.delete'),
      okType: 'danger',
      cancelText: t('workspace.agents.form.cancel'),
      onOk: () =>
        deleteMutation
          .mutateAsync(agent.id)
          .then(() => message.success(t('workspace.agents.delete.success')))
          .catch((e) => showApiErrors(e))
    })

  const columns: TableColumnsType<WorkspaceAgent> = [
    {
      title: t('workspace.agents.columns.name'),
      key: 'name',
      fixed: 'left',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (_, agent) => (
        <Flex align="flex-start" gap={10} style={{ minWidth: 0 }}>
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              background: 'var(--ant-color-fill-secondary)',
              color: 'var(--ant-color-text-secondary)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 auto',
              marginTop: 2
            }}
          >
            <Bot size={15} />
          </span>
          <div style={{ minWidth: 0 }}>
            <Text strong style={{ fontSize: 13, display: 'block' }}>
              {agent.name}
            </Text>
            {agent.description ? (
              <Text
                type="secondary"
                style={{ fontSize: 12, display: 'block' }}
                ellipsis
              >
                {agent.description}
              </Text>
            ) : null}
          </div>
        </Flex>
      )
    },
    {
      title: t('workspace.agents.columns.connections'),
      key: 'connections',
      width: 130,
      render: (_, agent) =>
        (agent.mcpServerCount ?? 0) > 0 ? (
          <Text style={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
            {agent.mcpServerCount}
          </Text>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('workspace.agents.noConnections')}
          </Text>
        )
    },
    {
      title: t('workspace.agents.columns.created'),
      key: 'created',
      width: 120,
      render: (_, agent) => (
        <DateTimeCell
          value={agent.createdAt}
          variant="date"
          style={{ fontSize: 12 }}
        />
      )
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      width: 120,
      align: 'right',
      fixed: 'right',
      render: (_, agent) => (
        // Row is clickable (onRow → openDetail); keep action clicks from
        // bubbling up so they don't also trigger the row navigation.
        <Flex gap={4} justify="flex-end" onClick={(e) => e.stopPropagation()}>
          <Tooltip title={t('workspace.agents.actions.open')}>
            <Button
              size="small"
              icon={<ExternalLink size={14} />}
              onClick={() => openDetail(agent)}
              aria-label={t('workspace.agents.actions.open')}
            />
          </Tooltip>
          {mayManageAgents ? (
            <>
              <Tooltip title={t('workspace.agents.actions.configure')}>
                <Button
                  size="small"
                  icon={<Settings size={14} />}
                  disabled={agent.canEdit === false}
                  onClick={() =>
                    navigate(paths.workspaces.agentEdit(id, agent.id))
                  }
                  aria-label={t('workspace.agents.actions.configure')}
                />
              </Tooltip>
              <Tooltip title={t('workspace.agents.actions.delete')}>
                <Button
                  size="small"
                  danger
                  icon={<Trash2 size={14} />}
                  disabled={agent.canEdit === false}
                  onClick={() => confirmDelete(agent)}
                  aria-label={t('workspace.agents.actions.delete')}
                />
              </Tooltip>
            </>
          ) : null}
        </Flex>
      )
    }
  ]

  // KPI strip. Agents + Pending approvals are real; the rest are gated on a
  // backend that doesn't exist yet (the Squad agent record has no
  // service/delegated type, status, or token expiry), so they show a "Soon" tag.
  const kpiCards: {
    key: string
    label: string
    value?: number | string
    soon?: boolean
  }[] = [
    {
      key: 'agents',
      label: t('workspace.agents.kpi.agents'),
      value: agents.length
    },
    { key: 'active', label: t('workspace.agents.kpi.active'), soon: true },
    {
      key: 'delegated',
      label: t('workspace.agents.kpi.delegated'),
      soon: true
    },
    {
      key: 'expiringSoon',
      label: t('workspace.agents.kpi.expiringSoon'),
      soon: true
    },
    {
      key: 'pendingApprovals',
      label: t('workspace.agents.kpi.pendingApprovals'),
      value: pendingApprovals.isLoading
        ? '…'
        : pendingApprovals.isError
          ? '—'
          : (pendingApprovals.data ?? 0)
    }
  ]

  return (
    <WorkspacePageShell
      sectionTitle={t('workspace.agents.title')}
      sectionDescription={t('workspace.agents.description')}
    >
      <AgentsHowItWorks />

      {isError ? (
        <QueryErrorAlert onRetry={() => void refetch()} />
      ) : isLoading ? (
        <ContentSpinner minHeight={240} />
      ) : (
        <>
          <Card variant="outlined" styles={{ body: { padding: 0 } }}>
            <Flex wrap="wrap">
              {kpiCards.map((kpi, i) => (
                <Flex key={kpi.key} flex={1} style={{ minWidth: 140 }}>
                  {i > 0 ? (
                    <div
                      style={{
                        width: 1,
                        alignSelf: 'stretch',
                        background: 'var(--ant-color-border-secondary)'
                      }}
                    />
                  ) : null}
                  <KpiCell
                    label={kpi.label}
                    value={kpi.value}
                    soon={kpi.soon}
                  />
                </Flex>
              ))}
            </Flex>
          </Card>

          <ProTableContainer<WorkspaceAgent>
            showSearch={false}
            rowKey="id"
            columns={columns}
            dataSource={filtered}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('workspace.agents.empty')}
                    </Text>
                  }
                />
              )
            }}
            pagination={{
              defaultPageSize: DEFAULT_PER_PAGE,
              pageSizeOptions: DEFAULT_PER_PAGE_OPTIONS.map(String),
              size: 'small',
              showSizeChanger: true,
              hideOnSinglePage: true
            }}
            onRow={(agent) => ({
              role: 'button',
              tabIndex: 0,
              'aria-label': t('workspace.agents.list.openRow', {
                name: agent.name
              }),
              style: { cursor: 'pointer' },
              onClick: () => openDetail(agent),
              onKeyDown: (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  openDetail(agent)
                }
              }
            })}
            scroll={{ x: 720 }}
            leftChildren={
              <Input
                size="small"
                allowClear
                prefix={<Search size={14} />}
                placeholder={t('workspace.agents.list.search')}
                style={{ width: FILTER_CONTROL_WIDTH }}
                value={q}
                onChange={(e) => setQuery(e.target.value)}
              />
            }
            rightChildren={
              mayManageAgents ? (
                <Button
                  size="small"
                  type="primary"
                  icon={<Plus size={14} />}
                  onClick={() => navigate(paths.workspaces.agentNew(id))}
                >
                  {t('workspace.agents.register')}
                </Button>
              ) : null
            }
          />
        </>
      )}
    </WorkspacePageShell>
  )
}
